import React, { useState, useMemo, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AdminLayout, { ADMIN_MENU } from "../components/admin/AdminLayout";
import DashboardSection from "../components/admin/sections/DashboardSection";
import MedewerkersSection from "../components/admin/sections/MedewerkersSection";
import AuditSection from "../components/admin/sections/AuditSection";
import KlantenSection from "../components/admin/sections/KlantenSection";
import AgentManagementSection from "../components/admin/sections/AgentManagementSection";
import AgentOperationsSection from "../components/admin/agent-operations/AgentOperationsSection";
import OffertesSection from "../components/admin/sections/OffertesSection";
import PickupSection from "../components/admin/sections/PickupSection";
import RemindersSection from "../components/admin/sections/RemindersSection";
import CallLogSection from "../components/admin/sections/CallLogSection";
import StatusGeneratorSection from "../components/admin/sections/StatusGeneratorSection";
import ShippingLabelsSection from "../components/admin/sections/ShippingLabelsSection";
import ConsolidationsSection from "../components/admin/sections/ConsolidationsSection";
import TrackingAutomationSection from "../components/admin/sections/TrackingAutomationSection";
import WarehouseSection from "../components/admin/sections/WarehouseSection";
import WebsiteMessagesSection from "../components/admin/operational/WebsiteMessagesSection";
import PrealertsSection from "../components/admin/operational/PrealertsSection";
import SalesInvoicesSection from "../components/admin/sections/SalesInvoicesSection";
import DebiteurenbeheerSection from "../components/admin/sections/DebiteurenbeheerSection";
// BetalingsregelingenSection blijft geïmporteerd voor mogelijke standalone-weergave,
// maar wordt in de reguliere flow gerenderd via DebiteurenbeheerSection.
// (Feb 2026 IA-refactor — geen breaking changes.)
import CommunicatieSection from "../components/admin/sections/CommunicatieSection";
import WebsiteSection from "../components/admin/sections/WebsiteSection";
import WebshopSection from "../components/admin/sections/WebshopSection";
import BestellingenSection from "../components/admin/sections/BestellingenSection";
import InstellingenSection from "../components/admin/sections/InstellingenSection";
import LeadsSection from "../components/admin/sections/LeadsSection";
import DocumentenSection from "../components/admin/sections/DocumentenSection";
import BounceCenterSection from "../components/admin/sections/BounceCenterSection";
import PlaceholderSection from "../components/admin/sections/PlaceholderSection";
import ReminderAlertProvider from "../components/admin/ReminderAlertProvider";

const PHASE2_DESCRIPTORS = {
  websitebeheer: {
    title: "Websitebeheer",
    description: "Beheer publieke websitepagina's",
    items: ["Per pagina: titel, slug, meta description, hero, content-blokken", "Versiehistorie met rollback", "Multi-language NL/EN"],
  },
  webshopbeheer: {
    title: "Webshopbeheer",
    description: "Beheer webshop producten, varianten en voorraad",
    items: ["Producten + varianten (maat, kleur), prijzen, BTW", "Categorieën", "Voorraad"],
  },
  instellingen: {
    title: "Instellingen",
    description: "Bedrijfs- en systeeminstellingen",
    items: ["Bedrijfsgegevens (synced met /api/config)", "Stripe live/test keys, FENEX-tekst, openingstijden", "2FA configuratie"],
  },
};

export default function AdminPage() {
  const { user, loading, hasPermission } = useAuth();
  const location = useLocation();

  // Deeplink-ondersteuning: lees `section` en optionele payload-params (zoals
  // `order_id`) uit de query-string zodat externe links (bevestigingsmail naar
  // shop@5999Cargo.nl, dispatch-mails, etc.) direct in de juiste sectie/detail
  // landen.
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const legacyContactId = location.hash.startsWith("#contact/") ? decodeURIComponent(location.hash.slice(9)) : null;
  const queryDeepSection = queryParams.get("section") || (legacyContactId ? "website_messages" : null);
  const queryOrderId = queryParams.get("order_id");
  const queryCustomerId = queryParams.get("customer_id");
  const queryInvoiceId = queryParams.get("invoice_id");
  const queryQuoteId = queryParams.get("quote_id");
  const queryShipmentId = queryParams.get("shipment_id");
  const queryBookingId = queryParams.get("booking_id");
  const queryConsolidationId = queryParams.get("consolidation_id");

  // Sprint A2 (22 Feb 2026) — Backward-compat aliases voor deep-links naar
  // secties die na de Feb 2026 IA-refactor onderdeel werden van een andere
  // sectie (subtabblad). `/admin?section=regelingen` moet nog steeds werken
  // want er zijn interne bookmarks + oudere e-mail-links die dit gebruiken.
  //
  // Iteratie A (28 Feb 2026) — extra aliases voor entity-search:
  //   Backend adapters gebruiken 'boekingen' / 'zendingen' als section-key
  //   in oudere responses/URLs. Zonder deze aliases faalde de deep-link en
  //   viel initialActive terug op de eerste ADMIN_MENU-key (dashboard voor
  //   Owner) — waardoor iedere zoekklik in Dashboard eindigde.
  const DEEP_LINK_ALIASES = {
    regelingen: "debiteuren",
    boekingen: "documenten",
    zendingen: "tracking_labels",
    consolidaties: "consolidations",
  };

  const initialActive = useMemo(() => {
    if (["website_messages", "prealerts"].includes(queryDeepSection)) return queryDeepSection;
    if (queryDeepSection) {
      // Directe match op menu
      const menu = ADMIN_MENU.find((m) => m.key === queryDeepSection);
      if (menu && hasPermission(menu.perm)) return menu.key;
      // Alias-match: bv "regelingen" → "debiteuren", "boekingen" → "documenten"
      const aliasTarget = DEEP_LINK_ALIASES[queryDeepSection];
      if (aliasTarget) {
        const aliasMenu = ADMIN_MENU.find((m) => m.key === aliasTarget);
        // Sprint A2 · Iteratie A: 'regelingen' behoudt eigen key voor sub-tab
        // routing (blijft `active === 'regelingen'`); overige aliases
        // resolven direct naar de target-section-key zodat de body-handler
        // de juiste sectie rendert. Voorkomt dashboard-fallback op ontbrekende
        // handlers voor de alias-key zelf.
        if (aliasMenu && hasPermission(aliasMenu.perm)) {
          if (queryDeepSection === "regelingen") return queryDeepSection;
          return aliasMenu.key;
        }
      }
    }
    const allowed = ADMIN_MENU.find((m) => hasPermission(m.perm));
    return allowed ? allowed.key : "dashboard";
  }, [user, queryDeepSection, hasPermission]);

  const [active, setActive] = useState(initialActive);
  // P1 — Sectie-prefilter (bv. vanuit klant-activiteitkaart navigeren naar
  // Facturen of Tracking gefilterd op die klant). Wordt automatisch gewist
  // wanneer een nieuwe sectie via het menu wordt geopend.
  const [sectionFilter, setSectionFilter] = useState(null);

  // Wrapper: setActiveSection(key, optionalFilter) — als geen filter wordt
  // meegegeven blijft de oude filter actief wanneer het dezelfde sectie is,
  // maar wissen bij wisseling van sectie via menu/sidebar.
  const navigateWithFilter = (key, filter) => {
    setActive(key);
    if (filter !== undefined) {
      setSectionFilter(filter);
    } else {
      setSectionFilter(null);
    }
  };

  useEffect(() => {
    setActive(initialActive);
  }, [initialActive]);

  if (loading) {
    return <div className="py-32 text-center text-gray-400">Laden...</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  // Find the requested menu item and verify permission client-side
  // (server-side check is also enforced on every API call).
  const menuItem = ADMIN_MENU.find((m) => m.key === active);
  // Manager has no settings:view but is allowed inside Instellingen to
  // manage briefpapier. The sub-tab gating in InstellingenSection still
  // hides Productieconfiguratie for non-Owner/Admin.
  const allowed = menuItem
    ? (hasPermission(menuItem.perm)
       || (menuItem.key === "instellingen" && user?.role === "manager"))
    : false;

  let body;
  // Sprint A2 backward-compat aliases: keys die niet in ADMIN_MENU staan
  // maar wel deep-link naar een sectie moeten routen (nu een tab in een
  // andere sectie). De permission-check is al gedaan in `initialActive`
  // (DEEP_LINK_ALIASES). We renderen deze BOVEN de menuItem-guard zodat
  // ze niet in de "Geen toegang" branch vallen.
  if (active === "regelingen") {
    // Feb 2026 IA-refactor: regelingen is nu een tab BINNEN Debiteurenbeheer.
    // Backward-compat voor deep-links en bookmarks.
    body = <DebiteurenbeheerSection initialTab="regelingen" />;
  } else if (!menuItem || !allowed) {
    body = (
      <div className="bg-white border border-gray-200 p-10 text-center" data-testid="admin-no-access">
        <h2 className="font-chivo font-black text-xl text-[#082c50]">Geen toegang</h2>
        <p className="text-sm text-gray-600 mt-2">
          Je hebt geen rechten om deze module te bekijken.
        </p>
      </div>
    );
  } else if (active === "dashboard") {
    body = <DashboardSection setActive={setActive} />;
  } else if (active === "medewerkers") {
    body = <MedewerkersSection />;
  } else if (active === "audit") {
    body = <AuditSection />;
  } else if (active === "agents") {
    body = <AgentManagementSection />;
  } else if (active === "agent_operations" || active === "agent_reconciliation") {
    body = <AgentOperationsSection key={active} reconciliation={active === "agent_reconciliation"} />;
  } else if (active === "klanten") {
    body = <KlantenSection onNavigateSection={navigateWithFilter} initialCustomerId={queryCustomerId} />;
  } else if (active === "offertes") {
    body = <OffertesSection initialQuoteId={queryQuoteId} />;
  } else if (active === "pickup") {
    body = <PickupSection />;
  } else if (active === "reminders") {
    body = <RemindersSection />;
  } else if (active === "call_log" || active === "telefoonregistratie") {
    body = <CallLogSection />;
  } else if (active === "status_generator") {
    body = <StatusGeneratorSection />;
  } else if (active === "tracking_labels") {
    body = <ShippingLabelsSection prefilter={sectionFilter} onClearPrefilter={() => setSectionFilter(null)} initialShipmentId={queryShipmentId} />;
  } else if (active === "warehouse") {
    body = <WarehouseSection initialShipmentId={queryShipmentId} initialPrealertId={queryParams.get("prealert_id")} />;
  } else if (active === "prealerts") {
    body = <PrealertsSection initialPrealertId={queryParams.get("prealert_id")} />;
  } else if (active === "website_messages") {
    body = <WebsiteMessagesSection initialMessageId={queryParams.get("message_id") || legacyContactId} />;
  } else if (active === "consolidations") {
    body = <ConsolidationsSection initialConsolidationId={queryConsolidationId} />;
  } else if (active === "tracking_automation" || active === "consolidation_automation") {
    body = <TrackingAutomationSection />;
  } else if (active === "facturen") {
    body = <SalesInvoicesSection prefilter={sectionFilter} onClearPrefilter={() => setSectionFilter(null)} initialInvoiceId={queryInvoiceId} />;
  } else if (active === "debiteuren") {
    body = <DebiteurenbeheerSection onOpenComposer={(cid, invId) => setSectionFilter({ invoice_id: invId, customer_id: cid })} />;
  } else if (active === "communicatie") {
    body = <CommunicatieSection />;
  } else if (active === "websitebeheer") {
    body = <WebsiteSection />;
  } else if (active === "webshopbeheer") {
    body = <WebshopSection />;
  } else if (active === "bestellingen") {
    body = <BestellingenSection initialOrderId={queryOrderId} />;
  } else if (active === "instellingen") {
    body = <InstellingenSection />;
  } else if (active === "leads") {
    body = <LeadsSection />;
  } else if (active === "documenten") {
    body = <DocumentenSection initialBookingId={queryBookingId} />;
  } else if (active === "bounce_center") {
    body = <BounceCenterSection />;
  } else {
    const desc = PHASE2_DESCRIPTORS[active] || { title: menuItem.label, description: "" };
    body = (
      <PlaceholderSection
        title={desc.title}
        description={desc.description}
        items={desc.items}
        testId={`admin-section-${active}`}
      />
    );
  }

  return (
    <ReminderAlertProvider onNavigateToReminder={() => setActive("reminders")}>
      <AdminLayout active={active} setActive={(k) => navigateWithFilter(k)}>
        {body}
      </AdminLayout>
    </ReminderAlertProvider>
  );
}
